import React, { useState, useEffect } from "react";
import api from "../../../api/axiosInstance";

const PlanComparisonAdmin = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await api.get("/api/plans");
        setPlans(res.data || []);
      } catch (error) {
        console.error("Error fetching plans:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  // Unique feature list across all plans
  const allFeatures = Array.from(
    new Set(plans.flatMap(plan => plan.features.map(f => f.text)))
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Loading plan comparison...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* HEADER */}
      <div>
        <h1 className="text-3xl font-bold">Plan Comparison</h1>
        <p className="text-gray-500 text-sm">
          View and compare all pricing plans side by side
        </p>
      </div>

      {/* COMPARISON TABLE */}
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-4 font-semibold">Features</th>
              {plans.map(plan => (
                <th
                  key={plan._id}
                  className="text-center p-4 font-semibold"
                >
                  {plan.name}
                  {plan.isPopular && (
                    <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                      Popular
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="p-4 font-medium">Monthly Price</td>
              {plans.map(plan => (
                <td key={plan._id} className="text-center p-4">
                  <span className="font-bold text-lg">
                    ${plan.monthlyPrice || 0}
                  </span>
                </td>
              ))}
            </tr>
            <tr className="border-b">
              <td className="p-4 font-medium">Annual Price</td>
              {plans.map(plan => (
                <td key={plan._id} className="text-center p-4">
                  <span className="font-bold text-lg">
                    ${plan.annualPrice || 0}
                  </span>
                </td>
              ))}
            </tr>
            <tr className="border-b">
              <td className="p-4 font-medium">Trial Days</td>
              {plans.map(plan => (
                <td key={plan._id} className="text-center p-4">
                  {plan.trialDays || 0} days
                </td>
              ))}
            </tr>
            
            {/* FEATURES */}
            {allFeatures.map((feature, index) => (
              <tr key={index} className="border-b">
                <td className="p-4 font-medium">{feature}</td>
                {plans.map(plan => {
                  const planFeature = plan.features.find(f => f.text === feature);
                  return (
                    <td key={plan._id} className="text-center p-4">
                      {planFeature ? (
                        <span
                          className={
                            planFeature.included
                              ? "text-green-600"
                              : "text-gray-400 line-through"
                          }
                        >
                          {planFeature.included ? "✓" : "✗"}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            
            <tr className="border-b">
              <td className="p-4 font-medium">Status</td>
              {plans.map(plan => (
                <td key={plan._id} className="text-center p-4">
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      plan.isActive
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {plan.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* PLAN SUMMARY */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map(plan => (
          <div
            key={plan._id}
            className={`border rounded-xl p-6 ${
              plan.isPopular ? "ring-2 ring-blue-600" : ""
            }`}
          >
            {plan.isPopular && (
              <div className="text-center mb-4">
                <span className="bg-blue-600 text-white text-xs px-3 py-1 rounded-full">
                  Popular
                </span>
              </div>
            )}
            
            <h3 className="text-xl font-bold text-center mb-2">{plan.name}</h3>
            <p className="text-gray-500 text-center mb-4">{plan.description}</p>
            
            <div className="text-center mb-4">
              <span className="text-3xl font-bold">${plan.monthlyPrice || 0}</span>
              <span className="text-gray-500">/month</span>
            </div>
            
            <div className="text-center text-sm text-gray-500 mb-4">
              ${plan.annualPrice || 0}/year • {plan.trialDays || 0} days trial
            </div>
            
            <div className="text-center">
              <span
                className={`text-xs px-2 py-1 rounded-full ${
                  plan.isActive
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-200 text-gray-500"
                }`}
              >
                {plan.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlanComparisonAdmin;
